import { useState, useEffect, useContext, useRef } from 'react';

import { cmnContext } from '../../../common/CmnContext';
import { SaleListContext } from '../SaleListContext';
import { SaleBannerModel } from '../../../../models/sale/Sale';
import { SaleListBannerArrow } from './arrow/SaleListBannerArrow';
import { SaleListBannerIndicator } from './indicator/SaleListBannerIndicator';

export function SaleListBanner({ bannerList }: { bannerList: SaleBannerModel[] }) {
  const isPc = useContext(cmnContext).isPc;
  const user = useContext(SaleListContext).user;
  const setDetailSale = useContext(SaleListContext).setDetailSale;

  const total = bannerList.length;


  // 前後に1つずつクローン
  const clones = total > 1 ? [
    bannerList[total - 1],
    ...bannerList,
    bannerList[0],
  ] : bannerList;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);

  // idx は clones 配列のインデックス（1 = 最初の本物）
  const [idx, setIdx] = useState(1);
  const [noAnim, setNoAnim] = useState(false);
  const [autoKey, setAutoKey] = useState(0);

  const touchStartX = useRef(0);

  // PC: 左右に大きく見せる（コンテナ幅の25%ずつ）
  // SP: コンテナ幅の15%ずつ
  const PEEK = Math.round(containerW * (isPc ? 0.28 : 0.15));
  const itemW = containerW > 0 ? containerW - PEEK * 2 : 0;
  const translateX = itemW > 0 ? -(idx * itemW) + PEEK : 0;

  // 実インデックス（0始まり、ドット用）
  const realIdx = total > 0 ? ((idx - 1) % total + total) % total : 0;

  // コンテナ幅計測（total変化時に再計測）
  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerW(containerRef.current.offsetWidth);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [total]);

  // bannerList変化時にリセット
  useEffect(() => {
    setIdx(1);
    setNoAnim(false);
  }, [total]);

  // 自動スライド（6秒）
  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => {
      setIdx(i => i + 1);
    }, 6000);
    return () => clearInterval(timer);
  }, [autoKey, total]);

  // タブが再表示されたとき idx を正規化してリセット
  useEffect(() => {
    if (total <= 1) return;
    const handleVisibility = () => {
      if (!document.hidden) {
        setNoAnim(true);
        setIdx(i => ((i - 1) % total + total) % total + 1);
        setAutoKey(k => k + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [total]);

  // テレポート後にアニメーション再有効化
  useEffect(() => {
    if (noAnim) {
      const id = requestAnimationFrame(() => setNoAnim(false));
      return () => cancelAnimationFrame(id);
    }
  }, [noAnim]);

  // 無限ループ: クローン位置に到達したら瞬間移動
  const onTransitionEnd = () => {
    if (idx === 0) {
      setNoAnim(true);
      setIdx(total);
    } else if (idx === clones.length - 1) {
      setNoAnim(true);
      setIdx(1);
    }
  };

  const goNext = () => { setIdx(i => i + 1); setAutoKey(k => k + 1); };
  const goPrev = () => { setIdx(i => i - 1); setAutoKey(k => k + 1); };

  // スワイプ
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      diff > 0 ? goNext() : goPrev();
    }
  };

  // 中央バナーのアクション
  const onClickCenter = (banner: SaleBannerModel) => {
    if (banner.sale) {
      setDetailSale(banner.sale);
    } else if (banner.link) {
      window.location.href = banner.link;
    }
  };

  // クリック: 中央→開く / 左右→そのアイテムを中央へ
  const onClickItem = (banner: SaleBannerModel, i: number) => {
    if (i === idx) {
      onClickCenter(banner);
    } else {
      setIdx(i);
      setAutoKey(k => k + 1);
    }
  };

  if (total === 0) return null;

  // img がない場合は sale のサムネイルにフォールバック
  const getBannerImg = (banner: SaleBannerModel): string => {
    if (banner.img) return banner.img;
    if (banner.sale?.movie_thumb) return `/images/sale/thumb/${banner.sale.movie_thumb}`;
    return '';
  };

  const BG = '#a6b78b';

  const CAROUSEL_H = isPc ? 300 : 200;
  const SIDE_H = isPc
    ? Math.round(CAROUSEL_H * 0.7)
    : Math.round(CAROUSEL_H * 0.75);

  const imgStyle = (isCurrent: boolean): React.CSSProperties => ({
    display: 'block',
    width: 'auto',
    maxWidth: '100%',
    height: 'auto',
    maxHeight: isCurrent ? CAROUSEL_H : SIDE_H,
  });

  // バナー1枚: 従来の表示
  if (total === 1) {
    const b = bannerList[0];
    const bImg = getBannerImg(b);
    if (!bImg || (!b?.sale && !b?.link)) return null;
    return (
      <div
        style={{ padding: isPc ? '20px 27%' : '10px 7%', backgroundColor: BG, cursor: 'pointer' }}
        onClick={() => onClickCenter(b)}
      >
        <img src={bImg} style={imgStyle(true)} />
      </div>
    );
  }

  // カルーセル（3アイテム: 中央1 + 左右peek）
  return (
    <div style={{ background: 'linear-gradient(to bottom, #3a5228 0%, #7aaa55 40%, #7aaa55 60%, #3a5228 100%)', paddingTop: isPc ? 20 : 10 }}>

      {user?.name &&
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isPc ? 14 : 10,
          padding: isPc ? '4px 32px 14px' : '2px 16px 10px',
        }}>
          <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.45)' }} />
          <p style={{
            margin: 0,
            color: '#fff',
            fontSize: isPc ? 17 : 13,
            fontWeight: 'bold',
            letterSpacing: '0.1em',
            whiteSpace: 'nowrap',
            textShadow: '0 1px 6px rgba(0,0,0,0.5)',
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.55)',
            borderRadius: 50,
            padding: isPc ? '7px 22px' : '5px 16px',
          }}>{user.name}様におすすめの動画</p>
          <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.45)' }} />
        </div>
      }

      <div
        ref={containerRef}
        style={{ overflow: 'hidden', position: 'relative' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {containerW > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              transform: `translateX(${translateX}px)`,
              transition: noAnim ? 'none' : 'transform 0.35s ease',
              willChange: 'transform',
            }}
            onTransitionEnd={onTransitionEnd}
          >
            {clones.map((banner, i) => {
              const isCurrent = i === idx;
              const isLeft = i < idx;
              const angle = isPc ? 35 : 50;
              const rotateY = isCurrent ? 0 : isLeft ? angle : -angle;
              const transformOrigin = isCurrent ? 'center center' : isLeft ? 'right center' : 'left center';
              return (
                <div
                  key={i}
                  style={{
                    minWidth: itemW,
                    maxWidth: itemW,
                    height: CAROUSEL_H,
                    padding: isPc ? '0 12px' : '0 8px',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: `perspective(600px) rotateY(${rotateY}deg)`,
                    transformOrigin,
                    transition: noAnim ? 'none' : 'transform 0.35s ease',
                  }}
                  onClick={() => onClickItem(banner, i)}
                >
                  <img src={getBannerImg(banner)} style={imgStyle(isCurrent)} />
                </div>
              );
            })}
          </div>
        )}

        {/* 左右矢印 */}
        {containerW > 0 &&
          <SaleListBannerArrow
            direction="left"
            isPc={isPc}
            peek={PEEK}
            onClick={goPrev}
          />
        }
        {containerW > 0 &&
          <SaleListBannerArrow
            direction="right"
            isPc={isPc}
            peek={PEEK}
            onClick={goNext}
          />
        }
      </div>

      {/* ドットインジケーター */}
      <SaleListBannerIndicator
        total={total}
        realIdx={realIdx}
        onSelect={i => { setIdx(i + 1); setAutoKey(k => k + 1); }}
      />

    </div>
  );
}
